import React, { useState } from 'react';
import { 
  FileSpreadsheet, Download, Users, UserCheck, Building2,
  Calculator, Calendar, Info, CheckCircle, Loader2, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPORT_TYPES = [
  {
    id: 'soci',
    titolo: 'Elenco Soci',
    descrizione: 'Lista completa soci con dati anagrafici, date iscrizione/cessazione, qualifica. Formato CSV compatibile RUNTS.',
    icon: Users,
    colore: '#3B82F6',
    campiInclusi: ['Codice Fiscale', 'Cognome/Nome', 'Data nascita', 'Residenza', 'Data iscrizione', 'Qualifica'],
    piano: 'Premium+'
  },
  {
    id: 'volontari',
    titolo: 'Registro Volontari',
    descrizione: 'Registro volontari conforme D.Lgs. 117/2017 con numerazione progressiva e stato copertura assicurativa.',
    icon: UserCheck,
    colore: '#22C55E',
    campiInclusi: ['N. Progressivo', 'Codice Fiscale', 'Dati anagrafici', 'Date attività', 'Copertura RC'],
    piano: 'Premium+'
  },
  {
    id: 'organi-sociali',
    titolo: 'Organi Sociali',
    descrizione: 'Cariche sociali con nomenclatura RUNTS (Organo di Amministrazione, Organo di Controllo, ecc.).',
    icon: Building2,
    colore: '#8B5CF6',
    campiInclusi: ['Tipo Organo', 'Carica', 'Dati persona', 'Data nomina', 'Scadenza mandato'],
    piano: 'Premium+'
  },
  {
    id: 'riepilogo',
    titolo: 'Riepilogo Numerico',
    descrizione: 'Conteggi per la comunicazione annuale RUNTS: soci (persone fisiche/enti), volontari, lavoratori.',
    icon: Calculator,
    colore: '#F59E0B',
    campiInclusi: ['N. Soci persone fisiche', 'N. Soci enti', 'N. Volontari', 'N. Lavoratori'],
    piano: 'Premium+',
    isRiepilogo: true
  }
];

export default function ExportRUNTS() {
  const currentYear = new Date().getFullYear();
  const [annoRiferimento, setAnnoRiferimento] = useState(currentYear - 1);
  const [soloAttivi, setSoloAttivi] = useState(true);
  const [loading, setLoading] = useState(null);
  const [riepilogo, setRiepilogo] = useState(null);

  const handleExport = async (tipo) => {
    setLoading(tipo);

    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/export-runts/${tipo}?anno_riferimento=${annoRiferimento}`;
      
      if (tipo === 'volontari') {
        url += `&solo_attivi=${soloAttivi}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Errore nell\'export');
      }

      // Download il file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      
      // Estrai il filename dall'header o usa default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `RUNTS_${tipo}_${annoRiferimento}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      toast.success(`Export ${tipo} completato!`);
    } catch (error) {
      console.error('Errore:', error);
      toast.error(error.message || 'Errore durante l\'export');
    } finally {
      setLoading(null);
    }
  };

  const handleGetRiepilogo = async () => {
    setLoading('riepilogo');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/export-runts/riepilogo-numerico?anno_riferimento=${annoRiferimento}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Errore nel caricamento');
      }

      const data = await response.json();
      setRiepilogo(data);
      toast.success('Riepilogo caricato');
    } catch (error) {
      console.error('Errore:', error);
      toast.error(error.message || 'Errore');
    } finally {
      setLoading(null);
    }
  };

  const handleDownloadRiepilogoPDF = async () => {
    setLoading('riepilogo-pdf');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/export-runts/riepilogo-numerico/pdf?anno_riferimento=${annoRiferimento}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Errore nella generazione');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `RUNTS_Riepilogo_Numerico_${annoRiferimento}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      toast.success('PDF riepilogo scaricato!');
    } catch (error) {
      console.error('Errore:', error);
      toast.error(error.message || 'Errore');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="export-runts-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <FileSpreadsheet className="w-7 h-7 text-blue-600" />
          Export
        </h1>
        <p className="text-slate-500">Esporta i dati nel formato richiesto dal Registro Unico Nazionale del Terzo Settore</p>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">Comunicazione annuale RUNTS</h4>
              <p className="text-sm text-blue-700 mt-1">
                Entro il <strong>30 giugno di ogni anno</strong>, gli ETS iscritti al RUNTS devono comunicare 
                il numero di soci, volontari e lavoratori riferiti al 31/12 dell'anno precedente.
              </p>
              <a 
                href="https://servizi.lavoro.gov.it/runts" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
              >
                Vai al portale RUNTS <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selezione Anno */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Anno di Riferimento
          </CardTitle>
          <CardDescription>
            I dati saranno filtrati per l'anno selezionato (riferimento al 31/12)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-32">
              <Label htmlFor="anno">Anno</Label>
              <Input
                id="anno"
                type="number"
                min={2020}
                max={currentYear}
                value={annoRiferimento}
                onChange={(e) => setAnnoRiferimento(parseInt(e.target.value))}
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setAnnoRiferimento(currentYear - 1)}
              >
                {currentYear - 1}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setAnnoRiferimento(currentYear)}
              >
                {currentYear}
              </Button>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Checkbox 
                id="soloAttivi" 
                checked={soloAttivi}
                onCheckedChange={setSoloAttivi}
              />
              <Label htmlFor="soloAttivi" className="text-sm cursor-pointer">
                Solo volontari attivi
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grid Export Types */}
      <div className="grid md:grid-cols-2 gap-4">
        {EXPORT_TYPES.map((tipo) => {
          const Icon = tipo.icon;
          const isLoading = loading === tipo.id;
          
          return (
            <Card 
              key={tipo.id} 
              className="hover:shadow-md transition-shadow border-l-4"
              style={{ borderLeftColor: tipo.colore }}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${tipo.colore}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: tipo.colore }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800">{tipo.titolo}</h3>
                      <Badge variant="outline" className="text-xs">{tipo.piano}</Badge>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{tipo.descrizione}</p>
                    
                    {/* Campi inclusi */}
                    <div className="mt-3 flex flex-wrap gap-1">
                      {tipo.campiInclusi.map((campo, i) => (
                        <span 
                          key={i}
                          className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded"
                        >
                          {campo}
                        </span>
                      ))}
                    </div>
                    
                    <Button 
                      className="mt-4"
                      onClick={() => tipo.isRiepilogo ? handleGetRiepilogo() : handleExport(tipo.id)}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {tipo.isRiepilogo ? 'Caricamento...' : 'Generazione...'}
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          {tipo.isRiepilogo ? 'Visualizza Riepilogo' : 'Scarica CSV'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Riepilogo Numerico */}
      {riepilogo && (
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <CheckCircle className="w-5 h-5" />
              Riepilogo Numerico - Dati al {riepilogo.data_riferimento}
            </CardTitle>
            <CardDescription className="text-amber-700">
              Da comunicare al RUNTS entro il {riepilogo.istruzioni?.scadenza}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4">
              {/* Soci */}
              <div className="p-4 bg-white rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Soci</span>
                </div>
                <div className="text-3xl font-bold text-slate-800">
                  {riepilogo.riepilogo?.soci?.totale || 0}
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  <div>Persone fisiche: {riepilogo.riepilogo?.soci?.persone_fisiche || 0}</div>
                  <div>Enti: {riepilogo.riepilogo?.soci?.enti || 0}</div>
                </div>
              </div>

              {/* Volontari */}
              <div className="p-4 bg-white rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <UserCheck className="w-5 h-5 text-green-600" />
                  <span className="font-medium">Volontari</span>
                </div>
                <div className="text-3xl font-bold text-slate-800">
                  {riepilogo.riepilogo?.volontari?.totale || 0}
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Volontari attivi
                </div>
              </div>

              {/* Lavoratori */}
              <div className="p-4 bg-white rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">Lavoratori</span>
                </div>
                <div className="text-3xl font-bold text-slate-800">
                  {riepilogo.riepilogo?.lavoratori?.totale || 0}
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Dipendenti/collaboratori
                </div>
              </div>
            </div>

            {/* Istruzioni */}
            <div className="mt-4 p-3 bg-white rounded-lg border border-amber-200">
              <h4 className="font-medium text-amber-800 mb-2">Come comunicare i dati:</h4>
              <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                <li>Accedi al portale RUNTS con SPID o CIE</li>
                <li>Vai alla sezione "Anagrafica Ente"</li>
                <li>Aggiorna i campi con i numeri sopra indicati</li>
                <li>Conferma e invia</li>
              </ol>
            </div>

            {/* Pulsante Scarica PDF */}
            <div className="mt-4 flex justify-end">
              <Button 
                onClick={handleDownloadRiepilogoPDF}
                disabled={loading === 'riepilogo-pdf'}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {loading === 'riepilogo-pdf' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generazione PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Scarica PDF Riepilogo
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Note sul formato */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Note sul formato CSV</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2">
          <p><strong>Separatore:</strong> Punto e virgola (;)</p>
          <p><strong>Codifica:</strong> UTF-8 con BOM (compatibile Excel)</p>
          <p><strong>Date:</strong> Formato GG/MM/AAAA</p>
          <p><strong>Codice Fiscale:</strong> Maiuscolo, 16 caratteri</p>
          <p className="text-slate-500 mt-4">
            I file CSV possono essere aperti con Excel, LibreOffice Calc o importati direttamente 
            sul portale RUNTS seguendo le istruzioni del Ministero.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
